import { AxiosResponse } from 'axios';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { DataKi, DataKiParams } from '../domain/dataKi';
import { DataKi2ndRow } from '../domain/dataKi2ndRow';
import {
  buildMainFieldFilterParams,
  toForSijirituParams,
} from '../domain/dataKi/searchParams';
import { MAIN_FIELD_TYPE, Option, QueryParameter } from '../domain/schemas';

import {
  dataKiFilteredKiTagAverageTagIdsSelector,
  dataKiParamsSelector,
} from '../redux/server/dataKi';
import {
  DataKi2ndRowActionCreators,
  DataKi2ndRowActionTypes,
  FetchDataKi2ndRowAction,
  SearchDataKi2ndRowAction,
  ToggleDataKi2ndRowAction,
  singleDataKi2ndRowSelector,
} from '../redux/server/dataKi2ndRow';
import { settingsOptionsKiSearchConditionSelector } from '../redux/server/settingsOptionsKi';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { extractTagIdFromQueryValue } from '../utils/queryUtils';
import { validateQuery } from '../utils/validateQuery';
import { handleErrorSaga } from './errorSaga';

/**
 * 平均系の行かどうか判定するユーティリティ
 *
 * - 'kiTagAverage' はタグ全体平均行を示す特殊値
 * - 'onlyOneKiTagAverage_<id>' 系は特定タグのみ展開する平均行を示す
 *
 * 注意: 実際の tagIds 等のデータ結び付けは saga 側で行われるため、
 * ここでは文字列パターンの判定のみを行う（純粋関数）。
 */
const isKiTagAverageRow = (value: string | null): boolean =>
  value === 'kiTagAverage';

const isOnlyOneKiTagAverageRow = (value: string | null): boolean =>
  value != null && value.includes('onlyOneKiTagAverage');

const isExcludedKiTagAverageRow = (value: string | null): boolean =>
  value === 'excludedKiTagAverage';

/**
 * QueryParameterを元に検索条件を加工する
 * @param queryParams QueryParameter
 * @param searchParams 機種集計の検索条件
 * @returns 加工された検索条件
 */
export const queryParameterToFormConditions = (
  queryParams: QueryParameter,
  searchParams: DataKiParams,
  makerOptions: Option[] | undefined,
  tagId?: string,
  tagIds?: string[]
): DataKiParams => {
  // 店舗行の通常の検索条件
  const modifiedParams = {
    ...searchParams,
    mainField: MAIN_FIELD_TYPE.HL_MEI,
    containsAverage: false,
    sort: searchParams.sort === 'kiTushoMei' ? 'hlMei' : searchParams.sort,
  };

  if (isKiTagAverageRow(queryParams.value)) {
    return {
      ...modifiedParams,
      tagIds: tagIds,
      // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
      ...toForSijirituParams(searchParams),
    };
  }

  if (isExcludedKiTagAverageRow(queryParams.value)) {
    return {
      ...modifiedParams,
      tagIds: tagIds,
      isExcludeForTagIds: true,
      ...toForSijirituParams(searchParams),
    };
  }

  if (queryParams.value !== null && queryParams.value !== 'null') {
    // 各機種タグ平均合計行から展開行を表示する際にtagIdを渡す
    if (isOnlyOneKiTagAverageRow(queryParams.value) && tagId) {
      return {
        ...modifiedParams,
        tagIds: [tagId],
        // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
        ...toForSijirituParams(searchParams),
      };
    }

    const mainFieldParams = buildMainFieldFilterParams(
      queryParams,
      searchParams,
      makerOptions
    );

    // 平均行以外 → QueryParameterで絞り込んだ検索条件
    return {
      ...modifiedParams,
      // MEMO: queryParams.name (kiList / sisTypes / makers / makerGroups) を指定されたもののみに変更する
      ...mainFieldParams,
      // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
      ...toForSijirituParams(searchParams),
    };
  }

  // 平均行の場合 → 通常の検索条件
  return modifiedParams;
};

/**
 * QueryParameterを元にAPIから店舗行の内容を取得してdataKi2ndRowに登録する
 * @param api AxiosInstance
 * @param action Action
 */
function* searchDataKi2ndRowSaga(action: SearchDataKi2ndRowAction) {
  // APIリクエスト前に必要な情報を取得
  const searchParams = yield* customSelect(dataKiParamsSelector); // 現在の検索条件
  const settingsOptions = yield* customSelect(
    settingsOptionsKiSearchConditionSelector
  );
  const queryParameter = action.payload.params; // 検索条件を取得

  // タグ全体平均行の展開用：選択されたタグのtagIdsを取得
  const selectedTagIds: string[] = yield select(
    dataKiFilteredKiTagAverageTagIdsSelector
  );

  // 各機種タグ平均合計行の場合、queryParameter.valueからtagIdを抽出
  // queryParameter.value = 'onlyOneKiTagAverage_123' → tagId = '123'
  const tagId = extractTagIdFromQueryValue(queryParameter.value);
  // 検索条件を加工
  const params = queryParameterToFormConditions(
    queryParameter,
    searchParams,
    settingsOptions.makers,
    tagId,
    selectedTagIds
  );

  // APIにリクエストを行う
  yield put(
    DataKi2ndRowActionCreators.fetchDataKi2ndRowAction(queryParameter, params)
  );
}

/**
 * 店舗行を取得した結果を返す
 * @param api AxiosInstance
 * @param params 検索パラメータ（DataKiParams）
 */
export function* fetchDataKi2ndRowSaga(
  api: Api,
  action: FetchDataKi2ndRowAction
) {
  try {
    const params = action.payload.params; // 検索条件を取得
    yield put(
      DataKi2ndRowActionCreators.fetchDataKi2ndRowRequestAction(
        action.payload.queryParameter.value,
        params
      )
    );

    validateQuery(params);

    // 展開行データを取得
    const response: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(params)
    );

    // 取得した内容を店舗行に追加する
    const dataKi2ndRow: DataKi2ndRow = {
      requestParams: action.payload.queryParameter,
      setting: response.data.setting,
      data: response.data.data,
    };

    // 正常時取得したデータをdataKi2ndRowにDispatchする
    yield put(
      DataKi2ndRowActionCreators.fetchDataKi2ndRowSuccessAction(
        action.payload.queryParameter.value,
        dataKi2ndRow
      )
    );
  } catch (error: unknown) {
    yield put(DataKi2ndRowActionCreators.renewDataKi2ndRowAction());
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 指定したQueryParameterと一致する店舗行を表示・非表示する
 * @param action Action
 */
function* toggleDataKi2ndRowSaga(action: ToggleDataKi2ndRowAction) {
  const queryParameter = action.payload.params;
  const kiList = action.payload.kiList;

  // queryParameterがundefinedの場合何もしない
  if (queryParameter === undefined) return;

  // queryParameterをもとに店舗行を取得
  const now2ndRow: DataKi2ndRow | undefined = yield select(
    singleDataKi2ndRowSelector(queryParameter.value)
  );

  // 未取得の場合データを取得して表示する
  if (now2ndRow === undefined) {
    yield put(
      DataKi2ndRowActionCreators.searchDataKi2ndRowAction(
        queryParameter,
        kiList
      )
    );
    return;
  }

  // 取得済みの場合はデータを削除する
  yield put(
    DataKi2ndRowActionCreators.hideDataKi2ndRowAction(queryParameter.value)
  );
}

/**
 * SEARCH_DATA_KI_2NDROWがDispatchされた時にfetchDataKi2ndRowSagaを実行する
 * @param api AxiosInstance
 */
function* handleSearchDataKi2ndRowSaga() {
  yield takeEvery(
    DataKi2ndRowActionTypes.SEARCH_DATA_KI_2NDROW,
    searchDataKi2ndRowSaga
  );
}

/**
 * FETCH_DATA_KI_2NDROWがDispatchされた時にfetchDataKi2ndRowSagaを実行する
 * @param api AxiosInstance
 */
function* handleFetchDataKi2ndRowSaga(api: Api) {
  yield takeEvery(
    DataKi2ndRowActionTypes.FETCH_DATA_KI_2NDROW,
    fetchDataKi2ndRowSaga,
    api
  );
}

/**
 * TOGGLE_DATA_KI_2NDROWがDispatchされた時にtoggleDataKi2ndRowSagaを実行する
 */
function* handleToggleDataKi2ndRowSaga() {
  yield takeEvery(
    DataKi2ndRowActionTypes.TOGGLE_DATA_KI_2NDROW,
    toggleDataKi2ndRowSaga
  );
}

/**
 * 店舗行に関するタスクを実行する
 * @param context AxiosInstance
 */
export function* dataKi2ndRowSagas(context: { api: Api }) {
  yield fork(handleSearchDataKi2ndRowSaga); // 検索条件セット時店舗行データを取得
  yield fork(handleFetchDataKi2ndRowSaga, context.api); // 指定した検索条件で店舗行データを取得
  yield fork(handleToggleDataKi2ndRowSaga); // 店舗行を表示・非表示
}
